/**
 * CR114/CR201: Seed Assets Dialog
 * 
 * Quick-seed placeholder assets for testing.
 * CR201: Uses SafeAssetImage for previews.
 */

import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { useAppAssets } from "@/hooks/useAppAssets";
import { PLACEHOLDER_SVGS, ASSET_TYPE_LABELS } from "@/lib/asset-studio/types";
import { SafeAssetImage } from "@/components/ui/SafeAssetImage";

interface SeedAssetsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SeedAssetsDialog({ open, onOpenChange }: SeedAssetsDialogProps) {
  const { createAsset, saving } = useAppAssets();
  const [selected, setSelected] = useState<string[]>(Object.keys(PLACEHOLDER_SVGS));
  const [seeding, setSeeding] = useState(false);

  const toggleSelection = (key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    if (selected.length === Object.keys(PLACEHOLDER_SVGS).length) {
      setSelected([]);
    } else {
      setSelected(Object.keys(PLACEHOLDER_SVGS));
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    let successCount = 0;
    let skippedCount = 0;
    
    // CR337: Fetch existing assets to prevent duplicates
    const { data: existingAssets } = await (await import("@/integrations/supabase/client")).supabase
      .from("app_assets")
      .select("name, type");
    
    const existingSet = new Set(
      (existingAssets || []).map((a: { name: string; type: string }) => `${a.type}::${a.name}`)
    );
    
    for (const key of selected) {
      const placeholder = PLACEHOLDER_SVGS[key];
      if (placeholder) {
        // Skip if already exists
        const dedupKey = `${placeholder.type}::${placeholder.name}`;
        if (existingSet.has(dedupKey)) {
          skippedCount++;
          continue;
        }
        const result = await createAsset({
          name: placeholder.name,
          type: placeholder.type,
          svg: placeholder.svg,
          tags: placeholder.tags,
          is_tileable: placeholder.tags.includes("tileable"),
        });
        if (result) successCount++;
      }
    }

    if (skippedCount > 0) {
      toast.info(`Skipped ${skippedCount} asset(s) already in studio`);
    }

    setSeeding(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Seed Placeholder Assets
          </DialogTitle>
          <DialogDescription>
            Create placeholder SVG assets for testing Theme Labs skins.
            These can be replaced with real artwork later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Select assets to create:</Label>
            <Button variant="ghost" size="sm" onClick={toggleAll}>
              {selected.length === Object.keys(PLACEHOLDER_SVGS).length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {Object.entries(PLACEHOLDER_SVGS).map(([key, placeholder]) => (
              <div
                key={key}
                className="flex items-center gap-3 p-2 rounded border hover:bg-muted/50 cursor-pointer"
                onClick={() => toggleSelection(key)}
              >
                <Checkbox
                  checked={selected.includes(key)}
                  onCheckedChange={() => toggleSelection(key)}
                />
                {/* CR201: Use SafeAssetImage instead of dangerouslySetInnerHTML */}
                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  <SafeAssetImage
                    svg={placeholder.svg}
                    alt={placeholder.name}
                    className="w-full h-full"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{placeholder.name}</div>
                  <Badge variant="outline" className="text-[10px]">
                    {ASSET_TYPE_LABELS[placeholder.type]}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSeed}
            disabled={selected.length === 0 || seeding || saving}
          >
            {seeding ? "Creating..." : `Create ${selected.length} Assets`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
